// @ts-nocheck
import { Group, Notification, FriendRequest } from '../types';
import { db as firestore } from '../firebaseConfig';
import { doc, setDoc, collection, deleteDoc, getDoc, updateDoc, arrayUnion, arrayRemove, query, where, getDocs } from 'firebase/firestore';

/**
 * Create a notification helper (Writes to Firestore)
 */
export async function createNotification(
    setDb: Function, // Kept for compatibility but unused for global sync
    groupId: string, // Kept for compatibility
    playerId: string, // Recipient
    type: Notification['type'],
    message: string,
    actionUrl?: string,
    actionData?: Notification['actionData'],
    senderId?: string
): Promise<Notification | undefined> {
    const notificationId = `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const notification: Notification = {
        id: notificationId,
        playerId,
        senderId: senderId || null,
        groupId,
        type,
        title: getNotificationTitle(type),
        message,
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        read: false,
        actionUrl: actionUrl || null,
        actionData: actionData || null
    };

    try {
        // Write to User's Notification Subcollection
        // This ensures security rules allow the user to read their own notifications
        // We use JSON.parse(JSON.stringify()) to remove any remaining undefined values just in case, 
        // though the explicit nulls above should handle it.
        const cleanNotification = JSON.parse(JSON.stringify(notification));
        await setDoc(doc(firestore, 'users', playerId, 'notifications', notificationId), cleanNotification);
        return notification;
    } catch (error) {
        console.error("Error creating notification:", error);
        return undefined;
    }
}

/**
 * Send a friend request (Global)
 */
export async function sendFriendRequest(
    setDb: Function,
    currentGroup: Group,
    fromPlayerId: string,
    toPlayerId: string,
    showToast: Function,
    t: any
): Promise<void> {
    // Check if a request already exists (in either direction)
    // Note: This check relies on the client's local db state for immediate feedback.
    // A more robust check would query Firestore, but we want speed.
    // We assume setDb is actually the db object from context in this specific call usage or we need to access db differently.
    // Wait, setDb is a setter. We need the current state.
    // The currentGroup has players, but not requests.
    // We will query Firestore to be safe and robust.

    const sentQuery = query(collection(firestore, 'users', fromPlayerId, 'friendRequests'), where('toPlayerId', '==', toPlayerId));
    const receivedQuery = query(collection(firestore, 'users', fromPlayerId, 'friendRequests'), where('fromPlayerId', '==', toPlayerId));

    const [sentSnap, receivedSnap] = await Promise.all([getDocs(sentQuery), getDocs(receivedQuery)]);

    if (!sentSnap.empty || !receivedSnap.empty) {
        showToast(t('inbox.social.friendRequestAlreadySent'), 'info');
        return;
    }

    const requestId = `friend_req_${Date.now()}`;
    const friendRequest: FriendRequest = {
        id: requestId,
        fromPlayerId,
        toPlayerId,
        status: 'pending',
        timestamp: new Date().toISOString()
    };

    try {
        // Write to Recipient's Friend Requests Subcollection
        await setDoc(doc(firestore, 'users', toPlayerId, 'friendRequests', requestId), friendRequest);
        // Write to Sender's Friend Requests Subcollection (so they see it as pending)
        await setDoc(doc(firestore, 'users', fromPlayerId, 'friendRequests', requestId), friendRequest);

        // Create notification for target player
        const fromPlayer = currentGroup.players.find(p => p.id === fromPlayerId);
        const notif = await createNotification(
            setDb,
            currentGroup.id,
            toPlayerId,
            'friend_request',
            t('inbox.social.friendRequestNotif', { name: fromPlayer?.name || t('common.unknownPlayer') }),
            undefined,
            { playerId: fromPlayerId, fromPlayerId, requestId }, // Pass requestId to handle accept/decline
            fromPlayerId
        );

        if (notif) {
            try {
                // Write a copy to the SENDER's subcollection so it appears in "Sent"
                // Keep original playerId (recipient) - this notification appears in "Sent" via senderId filter, not "Received"
                const sentNotif = { ...notif, read: true };
                const cleanSentNotif = JSON.parse(JSON.stringify(sentNotif));
                await setDoc(doc(firestore, 'users', fromPlayerId, 'notifications', notif.id), cleanSentNotif);
            } catch (e) {
                console.error("Error saving sent friend request notification:", e);
            }
        }

        showToast(t('inbox.social.friendRequestSent'));
    } catch (error) {
        console.error("Error sending friend request:", error);
        showToast(t('inbox.social.errorSendingRequest'), 'error');
    }
}

/**
 * Accept a friend request (User Subcollection)
 */
export async function acceptFriendRequest(
    setDb: Function,
    currentGroup: Group, // Unused but kept for signature
    requestId: string,
    showToast: Function,
    currentUserId: string, // Added currentUserId
    t: any
): Promise<void> {
    try {
        // Read from Recipient's (Current User's) Subcollection
        const reqRef = doc(firestore, 'users', currentUserId, 'friendRequests', requestId);
        const reqSnap = await getDoc(reqRef);

        if (!reqSnap.exists()) {
            showToast(t('inbox.social.requestNotFound'));
            return;
        }

        const request = reqSnap.data();
        const { fromPlayerId, toPlayerId } = request;

        // Update Friends List for BOTH users
        const fromUserRef = doc(firestore, 'users', fromPlayerId);
        const toUserRef = doc(firestore, 'users', toPlayerId);

        // We need to update the 'friends' array in the user document
        await updateDoc(fromUserRef, {
            friends: arrayUnion(toPlayerId)
        }).catch(() => setDoc(fromUserRef, { friends: [toPlayerId] }, { merge: true }));

        await updateDoc(toUserRef, {
            friends: arrayUnion(fromPlayerId)
        }).catch(() => setDoc(toUserRef, { friends: [fromPlayerId] }, { merge: true }));

        // Delete the request from Recipient
        await deleteDoc(reqRef);
        // Delete the request from Sender
        await deleteDoc(doc(firestore, 'users', fromPlayerId, 'friendRequests', requestId));

        // Get the name of the player who accepted (current user = toPlayerId)
        let acceptorName = t('common.unknownPlayer');

        // Try to find in currentGroup first
        const toPlayer = currentGroup.players?.find(p => p.id === toPlayerId);
        if (toPlayer?.name) {
            acceptorName = toPlayer.name;
        } else {
            // Fallback: fetch from Firestore users collection
            try {
                const userDoc = await getDoc(doc(firestore, 'users', toPlayerId));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    acceptorName = userData.pseudo || userData.name || t('common.unknownPlayer');
                }
            } catch (e) {
                console.error("Could not fetch acceptor name:", e);
            }
        }

        // Notify the requester (the one who sent the friend request)
        await createNotification(
            setDb,
            currentGroup.id,
            fromPlayerId,
            'friend_accepted',
            t('inbox.social.friendAccepted', { name: acceptorName }),
            undefined,
            { playerId: toPlayerId, fromPlayerId: toPlayerId },
            toPlayerId
        );

        showToast(t('inbox.social.nowFriends'));
    } catch (error) {
        console.error("Error accepting friend request:", error);
        showToast(t('inbox.social.errorAccepting'), 'error');
    }
}

/**
 * Decline a friend request (User Subcollection)
 */
export async function declineFriendRequest(
    setDb: Function,
    currentGroup: Group,
    requestId: string,
    showToast: Function,
    currentUserId: string,
    t: any,
    otherUserId?: string // The other party involved (the sender of the request)
): Promise<void> {
    try {
        // Get request details before deleting (to know who sent it)
        const reqRef = doc(firestore, 'users', currentUserId, 'friendRequests', requestId);
        const reqSnap = await getDoc(reqRef);

        let fromPlayerId: string | undefined;
        let toPlayerId: string | undefined;
        if (reqSnap.exists()) {
            const request = reqSnap.data();
            fromPlayerId = request.fromPlayerId;
            toPlayerId = request.toPlayerId;
        }

        // Delete from current user
        await deleteDoc(reqRef);

        // Delete from other user if provided
        if (otherUserId) {
            await deleteDoc(doc(firestore, 'users', otherUserId, 'friendRequests', requestId));
        }

        // --- Clean up friend_request notifications ---
        // Delete the notification from both parties so that stale notifications
        // don't pile up when a user repeatedly sends/cancels friend requests.
        const recipientId = toPlayerId || otherUserId;
        const senderId = fromPlayerId || otherUserId;

        // Find and delete notifications with this requestId from both users
        const usersToClean = new Set<string>();
        if (recipientId) usersToClean.add(recipientId);
        if (senderId) usersToClean.add(senderId);
        usersToClean.add(currentUserId);

        for (const userId of usersToClean) {
            try {
                const notifsQuery = query(
                    collection(firestore, 'users', userId, 'notifications'),
                    where('type', '==', 'friend_request'),
                    where('actionData.requestId', '==', requestId)
                );
                const notifsSnap = await getDocs(notifsQuery);
                for (const notifDoc of notifsSnap.docs) {
                    await deleteDoc(notifDoc.ref);
                }
            } catch (e) {
                console.error(`Error cleaning up friend request notifications for user ${userId}:`, e);
            }
        }

        // Notify the original sender that their request was declined
        // (Only when the RECIPIENT declines, not when the SENDER cancels their own request)
        const isSenderCancelling = currentUserId === fromPlayerId;
        const notifTarget = fromPlayerId || otherUserId;
        if (!isSenderCancelling && notifTarget && notifTarget !== currentUserId) {
            const currentPlayer = currentGroup.players.find(p => p.id === currentUserId);
            await createNotification(
                setDb,
                currentGroup.id,
                notifTarget,
                'system', // Use system type for declined (less harsh than a dedicated type)
                t('inbox.social.friendDeclinedNotif', { name: currentPlayer?.name || t('common.unknownPlayer') }),
                undefined,
                { playerId: currentUserId, fromPlayerId: currentUserId },
                currentUserId
            );
        }

        showToast(t('inbox.social.requestDeleted'));
    } catch (error) {
        console.error("Error declining friend request:", error);
        showToast(t('inbox.social.error'), 'error');
    }
}

/**
 * Send a direct message (Global)
 */
export async function sendDirectMessage(
    setDb: Function,
    currentGroup: Group,
    fromPlayerId: string,
    toPlayerId: string,
    message: string,
    showToast: Function,
    t: any
): Promise<void> {
    if (!message.trim()) {
        showToast(t('inbox.social.emptyMessage'));
        return;
    }

    const fromPlayer = currentGroup.players.find(p => p.id === fromPlayerId);
    const messageId = `dm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Create notification with the message
    const notif = await createNotification(
        setDb,
        currentGroup.id,
        toPlayerId,
        'direct_message',
        t('inbox.social.messageFrom', { name: fromPlayerId === fromPlayer?.id ? (fromPlayer?.name || t('common.unknownPlayer')) : t('common.unknownPlayer') }), // Use name if available
        undefined,
        {
            playerId: fromPlayerId,
            fromPlayerId,
            messageId,
            messageText: message
        },
        fromPlayerId
    );

    if (notif) {
        try {
            // Write a copy to the SENDER's subcollection so it appears in "Sent"
            // We need to ensure the ID is the same so we can track it if needed
            // The 'read' status for the sender should probably be true effectively, or irrelevant
            const sentNotif = { ...notif, read: true };
            const cleanSentNotif = JSON.parse(JSON.stringify(sentNotif));
            await setDoc(doc(firestore, 'users', fromPlayerId, 'notifications', notif.id), cleanSentNotif);
        } catch (e) {
            console.error("Error saving sent message:", e);
        }
    }

    showToast(t('inbox.social.messageSent'));
}

/**
 * Create a chat notification for all participants except sender
 */
export async function notifyMatchmakingChat(
    setDb: Function,
    currentGroup: Group,
    requestId: string,
    senderId: string,
    senderName: string
): Promise<void> {
    const request = currentGroup.matchmakingRequests?.find(r => r.id === requestId);
    if (!request) return;

    // Notify all participants except the sender
    for (const participantId of request.participants) {
        if (participantId !== senderId) {
            await createNotification(
                setDb,
                currentGroup.id,
                participantId,
                'chat_message',
                `Nouveau message de ${senderName} dans ${request.location}`,
                'MATCHMAKING',
                {
                    requestId,
                    fromPlayerId: senderId
                },
                senderId
            );
        }
    }
}

/**
 * Remove a friend (User Subcollection)
 */
export async function removeFriend(
    db: any, // Unused but kept for signature compatibility if needed
    currentUserId: string,
    friendId: string
): Promise<void> {
    try {
        const currentUserRef = doc(firestore, 'users', currentUserId);
        const friendUserRef = doc(firestore, 'users', friendId);

        await updateDoc(currentUserRef, {
            friends: arrayRemove(friendId)
        });

        await updateDoc(friendUserRef, {
            friends: arrayRemove(currentUserId)
        });
    } catch (error) {
        console.error("Error removing friend:", error);
        throw error;
    }
}

/**
 * Calculate friendship stats between two players
 */
export function getFriendshipStats(
    player1Id: string,
    player2Id: string,
    groups: Group[]
): { matchCount: number; level: number; levelName: string } {
    let matchCount = 0;

    groups.forEach(group => {
        // 1. Quick Matches
        (group.quickMatches || []).forEach(match => {
            const players = [match.team1Id, match.team2Id, match.p3Id, match.p4Id].filter(Boolean);
            if (players.includes(player1Id) && players.includes(player2Id)) {
                matchCount++;
            }
        });

        // 2. Tournament Matches
        (group.tournaments || []).forEach(tournament => {
            (tournament.matches || []).forEach(match => {
                const team1 = tournament.teams.find(t => t.id === match.team1Id);
                const team2 = tournament.teams.find(t => t.id === match.team2Id);
                const players = [
                    team1?.player1Id, team1?.player2Id,
                    team2?.player1Id, team2?.player2Id
                ].filter(Boolean);

                if (players.includes(player1Id) && players.includes(player2Id)) {
                    matchCount++;
                }
            });
        });
    });

    let level = 0;
    let levelName = 'Amis';

    if (matchCount >= 50) {
        level = 4;
        levelName = 'Inséparables';
    } else if (matchCount >= 20) {
        level = 3;
        levelName = 'Frères de Padel';
    } else if (matchCount >= 5) {
        level = 2;
        levelName = 'Partenaires';
    } else if (matchCount >= 1) {
        level = 1;
        levelName = 'Coéquipiers';
    }

    return { matchCount, level, levelName };
}

/**
 * Send a poke / reminder for a friend request
 */
export async function sendPoke(
    setDb: Function,
    currentGroup: Group,
    requestId: string,
    toPlayerId: string,
    fromPlayerId: string,
    showToast: Function,
    t: any
): Promise<void> {
    const pokeId = `poke_${requestId}_${Date.now()}`;

    // Check if a poke was already sent recently (optional, but good for spam prevention)
    // For now, we just send it.

    const fromPlayer = currentGroup.players.find(p => p.id === fromPlayerId);

    await createNotification(
        setDb,
        currentGroup.id,
        toPlayerId,
        'system', // Use system type for now, or add a new 'poke' type if needed
        t('inbox.social.pokeNotif', { name: fromPlayer?.name || t('common.unknownPlayer') }),
        undefined,
        { playerId: fromPlayerId, fromPlayerId, requestId, isPoke: true },
        fromPlayerId
    );

    showToast(t('inbox.social.reminderSent'));
}

function getNotificationTitle(type: Notification['type']): string {
    switch (type) {
        case 'matchmaking_new': return 'NOUVEAU MATCHMAKING';
        case 'matchmaking_accepted': return 'MATCHMAKING ACCEPTÉ';
        case 'matchmaking_declined': return 'MATCH REFUSÉ';
        case 'challenge_new': return 'NOUVEAU DÉFI';
        case 'challenge_accepted': return 'DÉFI ACCEPTÉ';
        case 'challenge_declined': return 'DÉFI REFUSÉ';
        case 'friend_request': return 'DEMANDE D\'AMI';
        case 'friend_accepted': return 'AMI ACCEPTÉ';
        case 'direct_message': return 'NOUVEAU MESSAGE';
        case 'chat_message': return 'NOUVEAU MESSAGE';
        case 'system': return 'INFO SYSTÈME';
        case 'team_invitation': return 'INVITATION ÉQUIPE';
        case 'team_invitation_response': return 'RÉPONSE INVITATION';
        case 'score_validation_request': return 'NOUVEAU SCORE À VALIDER 🎾';
        default: return type.replace('_', ' ').toUpperCase();
    }
}
